import React, { useState } from "react";
import { useLanguage } from "@/context/LanguageContext";
import { UtensilsCrossed } from "lucide-react";

const HERO_MENU_IMG =
  "https://images.pexels.com/photos/18574183/pexels-photo-18574183.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1100&w=1500";

// Per-dish photos for the Especialidades category, keyed by item index:
// 0 Quesabirrias → milanesa, 1 Carne Asada → grilled chicken,
// 3 Mojarra Frita → fried fish, 4 Tacos Ahogados → red-broth tacos
const ESP_PHOTOS = {
  0: "/images/esp-1.jpg",
  1: "/images/esp-3.jpg",
  3: "/images/esp-2.jpg",
  4: "/images/esp-4.jpg",
};

export default function RestaurantMenu() {
  const { t } = useLanguage();
  const categories = t("menu.categories");
  const [active, setActive] = useState(0);
  const cat = categories[active];
  const isEsp = cat.name === "Especialidades";
  const isSides = active === 4;

  return (
    <section id="menu" data-testid="menu-section" className="relative bg-[#F9F6F0] py-24 md:py-36">
      <div className="mx-auto max-w-7xl px-6 md:px-10">
        {/* Header */}
        <div className="grid lg:grid-cols-12 gap-10 items-end mb-12">
          <div className="lg:col-span-7">
            <span className="text-[12px] uppercase tracking-[0.22em] text-[#C05A3A] font-medium inline-flex items-center gap-2">
              <UtensilsCrossed className="h-4 w-4" strokeWidth={1.5} />
              {t("menu.eyebrow")}
            </span>
            <h2
              data-testid="menu-title"
              className="font-serif-display italic text-[#232321] text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.02] mt-4"
            >
              {t("menu.title")}
            </h2>
          </div>
          <p className="lg:col-span-5 text-[#5C5C59] text-base leading-relaxed">
            {t("menu.subtitle")}
          </p>
        </div>

        <div className="grid lg:grid-cols-12 gap-8 lg:gap-12">
          {/* Image card */}
          <div className="lg:col-span-5">
            <div className="relative aspect-[4/5] rounded-2xl overflow-hidden border border-[#E2D9CD] sticky top-28">
              <img
                src={HERO_MENU_IMG}
                alt="Tacos"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-x-0 bottom-0 p-6 bg-gradient-to-t from-[#232321]/80 to-transparent">
                <div className="text-[#F9F6F0]/80 text-[11px] uppercase tracking-[0.22em] mb-2">
                  Cocina La Hacienda
                </div>
                <div className="font-serif-display text-[#F9F6F0] text-2xl md:text-3xl">
                  Hecho a mano, todos los días.
                </div>
              </div>
            </div>
          </div>

          {/* Menu list */}
          <div className="lg:col-span-7">
            {/* Category tabs */}
            <div
              data-testid="menu-tabs"
              className="flex flex-wrap gap-2 mb-8 pb-4 border-b border-[#E2D9CD]"
            >
              {categories.map((c, i) => (
                <button
                  key={c.name}
                  data-testid={`menu-tab-${i}`}
                  onClick={() => setActive(i)}
                  className={`rounded-full px-4 py-2 text-[13px] font-medium transition-all ${
                    active === i
                      ? "bg-[#232321] text-[#F9F6F0]"
                      : "bg-transparent text-[#5C5C59] hover:text-[#232321] border border-[#E2D9CD]"
                  }`}
                >
                  {c.name}
                </button>
              ))}
            </div>

            <h3 className="font-serif-display text-[#232321] text-3xl md:text-4xl mb-6">
              {cat.name}
            </h3>

            {isSides && (
              <div data-testid="sides-photos" className="mb-8">
                <div className="text-[11px] uppercase tracking-[0.22em] text-[#C05A3A] font-medium mb-3">
                  {t("menu.sidesCaption")}
                </div>
                <div className="grid grid-cols-3 gap-3">
                  {["/images/sides-1.jpg", "/images/sides-2.jpg", "/images/sides-3.jpg"].map((src, i) => (
                    <div
                      key={src}
                      data-testid={`sides-photo-${i}`}
                      className="relative aspect-[3/4] rounded-xl overflow-hidden border border-[#E2D9CD] group"
                    >
                      <img
                        src={src}
                        alt="Postres y aguas frescas"
                        loading="lazy"
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            <ul className="divide-y divide-[#E2D9CD]">
              {cat.items.map((item, idx) => {
                const thumb = isEsp ? ESP_PHOTOS[idx] : null;
                return (
                  <li
                    key={item.name}
                    data-testid={`menu-item-${active}-${idx}`}
                    className="py-5 flex items-start justify-between gap-6"
                  >
                    <div className="flex items-start gap-4 flex-1">
                      {thumb && (
                        <div
                          data-testid={`menu-item-thumb-${active}-${idx}`}
                          className="h-16 w-16 md:h-20 md:w-20 shrink-0 rounded-xl overflow-hidden border border-[#E2D9CD]"
                        >
                          <img
                            src={thumb}
                            alt={item.name}
                            loading="lazy"
                            className="w-full h-full object-cover"
                          />
                        </div>
                      )}
                      <div className="flex-1">
                        <div className="font-serif-display text-[#232321] text-xl md:text-2xl">
                          {item.name}
                        </div>
                        <div className="text-[#5C5C59] text-sm mt-1 leading-relaxed">
                          {item.desc}
                        </div>
                      </div>
                    </div>
                    <div className="font-serif-display text-[#C05A3A] text-xl md:text-2xl whitespace-nowrap">
                      {item.price}
                    </div>
                  </li>
                );
              })}
            </ul>

            <p className="mt-8 text-[12px] italic text-[#5C5C59] max-w-xl">
              {t("menu.footnote")}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
